import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import PoiActionDetailedLocation from '@alltrails/amplitude/enums/PoiActionDetailedLocation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExploreServiceFilterCounts from '@alltrails/explore-service/hooks/useExploreServiceFilterCounts';
import ActivePOIRoutingOptions from '@alltrails/maps/components/POI/ActivePOIPopupOptions/ActivePOIRoutingOptions';
import POIResults from '@alltrails/maps/components/POI/POIResults/POIResults';
import SelectedPOI from '@alltrails/maps/components/POI/SelectedPOI/SelectedPOI';
import useMap from '@alltrails/maps/hooks/useMap';
import { updateActivePOI, updateShowPOIPopupMenu } from '@alltrails/maps/redux/reducer';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import { useHasCustomRouteEditPermission } from '@/hooks/useHasCustomRouteEditPermission';
import { useLogExploreCardViewed } from '@/utils/analyticsHelpers';
import { useLogPOISelected } from '@/utils/poiAnalyticsHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import FocusedPOIMapComponents from './FocusedPOIMapComponents';

type POIMapComponentsProps = {
  onClick?: (poi: POISearchResult) => void;
  poiLocation: PoiActionDetailedLocation;
  shouldNavigateToPOIUrl?: boolean;
};

const POIMapComponents = ({ onClick, poiLocation, shouldNavigateToPOIUrl }: POIMapComponentsProps) => {
  const map = useMap();
  const searchParams = useSearchParams();
  const languageRegionCode = useLanguageRegionCode();
  const experiments = useExperiments();
  const dispatch = useAppDispatch();
  const logPOISelected = useLogPOISelected();
  const logPOICardViewed = useLogExploreCardViewed('poi');
  const canAddToRoute = useHasCustomRouteEditPermission();
  const isEditing = useAppSelector(state => state.customRoute.isEditing);
  const { adminFilterCount, filterCount } = useExploreServiceFilterCounts();
  const isTapAnywhereEnabled = experiments['web-disco-rm-tap-anywhere']?.value === 'on';

  // Hide POIs when any filters are applied
  const hasFilters = (filterCount ?? 0) > 0 || (adminFilterCount ?? 0) > 0;
  const shouldShowPOIs = !hasFilters;

  const getPOIUrl = useCallback(
    (poi: POISearchResult) => {
      return wrapUrlSafe(`/explore/${poi.slug}?${searchParams.toString()}`, languageRegionCode);
    },
    [languageRegionCode, searchParams]
  );

  const onPOIClick = useCallback(
    (poi: POISearchResult) => {
      logPOISelected(poi, poiLocation);
      onClick?.(poi);
      if (isTapAnywhereEnabled) {
        // Pin clicks set the active POI in POIResults, but hover card clicks only reach this handler
        dispatch(updateActivePOI(poi));
        dispatch(updateShowPOIPopupMenu(true));
      }
    },
    [onClick, logPOISelected, poiLocation, isTapAnywhereEnabled, dispatch]
  );

  if (!map) {
    return null;
  }

  const poiResultUrl = !isTapAnywhereEnabled && shouldNavigateToPOIUrl ? getPOIUrl : undefined;

  return (
    <>
      {shouldShowPOIs && <POIResults getPOIUrl={poiResultUrl} onPOICardViewed={logPOICardViewed} onPOIClick={onPOIClick} />}
      <ActivePOIRoutingOptions canAddToRoute={isEditing && canAddToRoute} isTapAnywhereEnabled={isTapAnywhereEnabled} />
      <SelectedPOI />
      <FocusedPOIMapComponents />
    </>
  );
};

export default POIMapComponents;
